import { metaTitle } from '../../util/tabTitle';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import Breadcrumbs from '../../components/Breadcrumb';
import { logTypes } from '../../util/logTypes';
import { useGetOrganizationUsers } from '../../api-http/users';
import { useAuth } from '../../context/auth-context';
import { BaseUser } from '../../types/user.types';
import { useSnack } from '../../util/useSnack';
import ActivityLogsTable from './ActivityLogsTable';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  ManagementPageHeader,
  ManagementToolbar,
} from '../../components/ManagementPageLayout';

const today = new Date().toISOString().slice(0, 10);

const ActivityLogsPage: FC = () => {
  metaTitle('ActivityLogs');

  const [logType, setLogType] = useState<string>('');
  const [authoredBy, setAuthoredBy] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const { switchedOrgId } = useAuth();

  const {
    data: usersList,
    error: usersErrs,
    refetch: refetchUsers,
  } = useGetOrganizationUsers();

  const { showErrSnack } = useSnack();

  useEffect(() => {
    if (usersErrs) {
      showErrSnack(usersErrs.msg);
    }
  }, [switchedOrgId, usersErrs]);

  useEffect(() => {
    refetchUsers();
  }, [switchedOrgId]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[{ label: 'Activity & audit logs', to: '/admin/activity-log' }]}
      />
      <ManagementPageHeader
        title="Activity & audit logs"
        subtitle="Review important changes and actions across your organization."
      />
      <ManagementToolbar>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          sx={{ width: '100%', flexWrap: 'wrap', rowGap: 1.25 }}
          useFlexGap
        >
          <Stack direction="row" spacing={1.25} sx={{ flex: '1 1 300px' }}>
            <TextField
              type="date"
              size="small"
              sx={{
                flex: 1,
                minWidth: 145,
                '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
              }}
              id="startDate"
              name="startDate"
              title="startDate"
              label="From"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="date"
              size="small"
              sx={{
                flex: 1,
                minWidth: 145,
                '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
              }}
              id="endDate"
              name="endDate"
              title="endDate"
              label="To"
              value={endDate}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Stack>
          <FormControl size="small" sx={{ flex: '1 1 210px', minWidth: 190 }}>
            <InputLabel id="log-type-label">Log type</InputLabel>
            <Select
              labelId="log-type-label"
              id="logType"
              name="Log Type"
              title="Log Type"
              label="Log type"
              onChange={(e) => setLogType(e.target.value)}
              value={logType}
            >
              <MenuItem value="">All log types</MenuItem>
              {logTypes?.map((logType, index) => (
                <MenuItem key={index} value={logType}>
                  {logType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: '1 1 230px', minWidth: 210 }}>
            <InputLabel id="authored-by-label">Authored by</InputLabel>
            <Select
              labelId="authored-by-label"
              id="authoredBy"
              name="authoredBy"
              title="authoredBy"
              label="Authored by"
              onChange={(e) => setAuthoredBy(e.target.value)}
              value={authoredBy}
            >
              <MenuItem value="">All users</MenuItem>
              {usersList?.map(
                (user: Pick<BaseUser, '_id' | 'email' | 'name'>) => (
                  <MenuItem key={user._id} value={user._id}>
                    <Box>
                      <Box
                        component="span"
                        sx={{ display: 'block', fontWeight: 700, fontSize: 13 }}
                      >
                        {user.name}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          fontSize: 12,
                          color: '#64748b',
                        }}
                      >
                        {user.email}
                      </Box>
                    </Box>
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Stack>
      </ManagementToolbar>
      <ActivityLogsTable
        filterProps={{
          startDate,
          endDate,
          authoredUser: authoredBy,
          logType,
        }}
      />
    </Box>
  );
};

export default ActivityLogsPage;
